/**
 * OrgAudiencePage Duck
 *
 * State management for organization's own audience lists and subscribers.
 * Mirrors CreatorAudiencePage.duck.js but uses orgAudienceAPI with orgId.
 */

import { storableError } from '../../util/errors';
import { orgAudienceAPI } from '../../api/clients/orgAudienceAPI';
import { usersAPI } from '../../api/clients/usersAPI';

// ================ Action types ================ //

const ns = 'app/OrgAudiencePage';

export const SET_ORG_ID = `${ns}/SET_ORG_ID`;

export const FETCH_LISTS_REQUEST = `${ns}/FETCH_LISTS_REQUEST`;
export const FETCH_LISTS_SUCCESS = `${ns}/FETCH_LISTS_SUCCESS`;
export const FETCH_LISTS_ERROR = `${ns}/FETCH_LISTS_ERROR`;

export const CREATE_LIST_REQUEST = `${ns}/CREATE_LIST_REQUEST`;
export const CREATE_LIST_SUCCESS = `${ns}/CREATE_LIST_SUCCESS`;
export const CREATE_LIST_ERROR = `${ns}/CREATE_LIST_ERROR`;

export const UPDATE_LIST_REQUEST = `${ns}/UPDATE_LIST_REQUEST`;
export const UPDATE_LIST_SUCCESS = `${ns}/UPDATE_LIST_SUCCESS`;
export const UPDATE_LIST_ERROR = `${ns}/UPDATE_LIST_ERROR`;

export const DELETE_LIST_REQUEST = `${ns}/DELETE_LIST_REQUEST`;
export const DELETE_LIST_SUCCESS = `${ns}/DELETE_LIST_SUCCESS`;
export const DELETE_LIST_ERROR = `${ns}/DELETE_LIST_ERROR`;

export const FETCH_LIST_DETAIL_REQUEST = `${ns}/FETCH_LIST_DETAIL_REQUEST`;
export const FETCH_LIST_DETAIL_SUCCESS = `${ns}/FETCH_LIST_DETAIL_SUCCESS`;
export const FETCH_LIST_DETAIL_ERROR = `${ns}/FETCH_LIST_DETAIL_ERROR`;

export const ADD_MEMBER_TO_LIST_REQUEST = `${ns}/ADD_MEMBER_TO_LIST_REQUEST`;
export const ADD_MEMBER_TO_LIST_SUCCESS = `${ns}/ADD_MEMBER_TO_LIST_SUCCESS`;
export const ADD_MEMBER_TO_LIST_ERROR = `${ns}/ADD_MEMBER_TO_LIST_ERROR`;

export const IMPORT_MEMBERS_REQUEST = `${ns}/IMPORT_MEMBERS_REQUEST`;
export const IMPORT_MEMBERS_SUCCESS = `${ns}/IMPORT_MEMBERS_SUCCESS`;
export const IMPORT_MEMBERS_ERROR = `${ns}/IMPORT_MEMBERS_ERROR`;
export const CLEAR_IMPORT_ERRORS = `${ns}/CLEAR_IMPORT_ERRORS`;

export const FETCH_SUBSCRIBERS_REQUEST = `${ns}/FETCH_SUBSCRIBERS_REQUEST`;
export const FETCH_SUBSCRIBERS_SUCCESS = `${ns}/FETCH_SUBSCRIBERS_SUCCESS`;
export const FETCH_SUBSCRIBERS_ERROR = `${ns}/FETCH_SUBSCRIBERS_ERROR`;

export const REMOVE_MEMBER_REQUEST = `${ns}/REMOVE_MEMBER_REQUEST`;
export const REMOVE_MEMBER_SUCCESS = `${ns}/REMOVE_MEMBER_SUCCESS`;
export const REMOVE_MEMBER_ERROR = `${ns}/REMOVE_MEMBER_ERROR`;

export const FETCH_IMPORTS_REQUEST = `${ns}/FETCH_IMPORTS_REQUEST`;
export const FETCH_IMPORTS_SUCCESS = `${ns}/FETCH_IMPORTS_SUCCESS`;
export const FETCH_IMPORTS_ERROR = `${ns}/FETCH_IMPORTS_ERROR`;

export const ADD_BY_EMAIL_REQUEST = `${ns}/ADD_BY_EMAIL_REQUEST`;
export const ADD_BY_EMAIL_SUCCESS = `${ns}/ADD_BY_EMAIL_SUCCESS`;
export const ADD_BY_EMAIL_ERROR = `${ns}/ADD_BY_EMAIL_ERROR`;

export const IMPORT_SUBSCRIBERS_REQUEST = `${ns}/IMPORT_SUBSCRIBERS_REQUEST`;
export const IMPORT_SUBSCRIBERS_SUCCESS = `${ns}/IMPORT_SUBSCRIBERS_SUCCESS`;
export const IMPORT_SUBSCRIBERS_ERROR = `${ns}/IMPORT_SUBSCRIBERS_ERROR`;

export const MATCH_SUBSCRIBERS_REQUEST = `${ns}/MATCH_SUBSCRIBERS_REQUEST`;
export const MATCH_SUBSCRIBERS_SUCCESS = `${ns}/MATCH_SUBSCRIBERS_SUCCESS`;
export const MATCH_SUBSCRIBERS_ERROR = `${ns}/MATCH_SUBSCRIBERS_ERROR`;

export const FETCH_AUDIENCE_USERS_SUCCESS = `${ns}/FETCH_AUDIENCE_USERS_SUCCESS`;

export const RESET = `${ns}/RESET`;

// Email campaigns
export const FETCH_EMAILS_REQUEST = `${ns}/FETCH_EMAILS_REQUEST`;
export const FETCH_EMAILS_SUCCESS = `${ns}/FETCH_EMAILS_SUCCESS`;
export const FETCH_EMAILS_ERROR = `${ns}/FETCH_EMAILS_ERROR`;

export const CREATE_EMAIL_REQUEST = `${ns}/CREATE_EMAIL_REQUEST`;
export const CREATE_EMAIL_SUCCESS = `${ns}/CREATE_EMAIL_SUCCESS`;
export const CREATE_EMAIL_ERROR = `${ns}/CREATE_EMAIL_ERROR`;

export const UPDATE_EMAIL_REQUEST = `${ns}/UPDATE_EMAIL_REQUEST`;
export const UPDATE_EMAIL_SUCCESS = `${ns}/UPDATE_EMAIL_SUCCESS`;
export const UPDATE_EMAIL_ERROR = `${ns}/UPDATE_EMAIL_ERROR`;

export const DELETE_EMAIL_REQUEST = `${ns}/DELETE_EMAIL_REQUEST`;
export const DELETE_EMAIL_SUCCESS = `${ns}/DELETE_EMAIL_SUCCESS`;
export const DELETE_EMAIL_ERROR = `${ns}/DELETE_EMAIL_ERROR`;

export const SEND_EMAIL_REQUEST = `${ns}/SEND_EMAIL_REQUEST`;
export const SEND_EMAIL_SUCCESS = `${ns}/SEND_EMAIL_SUCCESS`;
export const SEND_EMAIL_ERROR = `${ns}/SEND_EMAIL_ERROR`;

// ================ Reducer ================ //

const initialState = {
  orgId: null,
  // Lists
  lists: [],
  listsLoading: false,
  listsLoaded: false,
  listsError: null,

  createListInProgress: false,
  createListError: null,

  updateListInProgress: false,
  updateListError: null,

  deleteListInProgress: null,
  deleteListError: null,

  selectedList: null,
  selectedListLoading: false,
  selectedListError: null,

  addMemberInProgress: false,
  addMemberError: null,

  importInProgress: false,
  importResult: null,
  importError: null,

  // Subscribers
  subscribers: [],
  subscribersLoading: false,
  subscribersLoaded: false,
  subscribersError: null,
  subscribersPagination: null,

  removeMemberInProgress: null,
  removeMemberError: null,

  imports: [],
  importsLoading: false,
  importsError: null,

  addByEmailInProgress: false,
  addByEmailError: null,

  importSubscribersInProgress: false,
  importSubscribersResult: null,
  importSubscribersError: null,

  matchInProgress: false,

  // Live Sharetribe user objects for matched subscribers, keyed by userId
  usersById: {},

  // Emails
  emails: [],
  emailsLoading: false,
  emailsLoaded: false,
  emailsError: null,
  createEmailInProgress: false,
  createEmailError: null,
  updateEmailInProgress: false,
  updateEmailError: null,
  deleteEmailInProgress: null,
  deleteEmailError: null,
  sendEmailInProgress: null,
  sendEmailError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_ORG_ID:
      // Switching orgs (same mounted component, different :orgId in the URL)
      // must drop the previous org's lists / subscribers / emails so they
      // can't flash under the new org while its fetches are in flight. Same
      // orgId → keep state so a revisit/refetch stays stale-while-revalidate.
      return payload === state.orgId ? state : { ...initialState, orgId: payload };
    case FETCH_LISTS_REQUEST:
      return { ...state, listsLoading: true, listsError: null };
    case FETCH_LISTS_SUCCESS:
      return { ...state, listsLoading: false, listsLoaded: true, lists: payload };
    case FETCH_LISTS_ERROR:
      return { ...state, listsLoading: false, listsError: payload };

    case CREATE_LIST_REQUEST:
      return { ...state, createListInProgress: true, createListError: null };
    case CREATE_LIST_SUCCESS:
      return { ...state, createListInProgress: false, lists: [...state.lists, payload] };
    case CREATE_LIST_ERROR:
      return { ...state, createListInProgress: false, createListError: payload };

    case UPDATE_LIST_REQUEST:
      return { ...state, updateListInProgress: true, updateListError: null };
    case UPDATE_LIST_SUCCESS:
      return {
        ...state,
        updateListInProgress: false,
        lists: state.lists.map(l => (l.id === payload.id ? payload : l)),
        selectedList:
          state.selectedList?.id === payload.id
            ? { ...state.selectedList, ...payload }
            : state.selectedList,
      };
    case UPDATE_LIST_ERROR:
      return { ...state, updateListInProgress: false, updateListError: payload };

    case DELETE_LIST_REQUEST:
      return { ...state, deleteListInProgress: payload, deleteListError: null };
    case DELETE_LIST_SUCCESS:
      return {
        ...state,
        deleteListInProgress: null,
        lists: state.lists.filter(l => l.id !== payload),
      };
    case DELETE_LIST_ERROR:
      return { ...state, deleteListInProgress: null, deleteListError: payload };

    case FETCH_LIST_DETAIL_REQUEST:
      return { ...state, selectedListLoading: true, selectedListError: null };
    case FETCH_LIST_DETAIL_SUCCESS:
      return { ...state, selectedListLoading: false, selectedList: payload };
    case FETCH_LIST_DETAIL_ERROR:
      return { ...state, selectedListLoading: false, selectedListError: payload };

    case ADD_MEMBER_TO_LIST_REQUEST:
      return { ...state, addMemberInProgress: true, addMemberError: null };
    case ADD_MEMBER_TO_LIST_SUCCESS:
      return { ...state, addMemberInProgress: false };
    case ADD_MEMBER_TO_LIST_ERROR:
      return { ...state, addMemberInProgress: false, addMemberError: payload };

    case CLEAR_IMPORT_ERRORS:
      return { ...state, importError: null, importSubscribersError: null };

    case IMPORT_MEMBERS_REQUEST:
      return { ...state, importInProgress: true, importResult: null, importError: null };
    case IMPORT_MEMBERS_SUCCESS:
      return { ...state, importInProgress: false, importResult: payload };
    case IMPORT_MEMBERS_ERROR:
      return { ...state, importInProgress: false, importError: payload };

    case FETCH_SUBSCRIBERS_REQUEST:
      return { ...state, subscribersLoading: true, subscribersError: null };
    case FETCH_SUBSCRIBERS_SUCCESS:
      return {
        ...state,
        subscribersLoading: false,
        subscribersLoaded: true,
        subscribers: payload.members,
        subscribersPagination: payload.pagination,
      };
    case FETCH_SUBSCRIBERS_ERROR:
      return { ...state, subscribersLoading: false, subscribersError: payload };

    case REMOVE_MEMBER_REQUEST:
      return { ...state, removeMemberInProgress: payload, removeMemberError: null };
    case REMOVE_MEMBER_SUCCESS:
      return {
        ...state,
        removeMemberInProgress: null,
        subscribers: state.subscribers.filter(s => s.id !== payload),
      };
    case REMOVE_MEMBER_ERROR:
      return { ...state, removeMemberInProgress: null, removeMemberError: payload };

    case FETCH_IMPORTS_REQUEST:
      return { ...state, importsLoading: true, importsError: null };
    case FETCH_IMPORTS_SUCCESS:
      return { ...state, importsLoading: false, imports: payload };
    case FETCH_IMPORTS_ERROR:
      return { ...state, importsLoading: false, importsError: payload };

    case ADD_BY_EMAIL_REQUEST:
      return { ...state, addByEmailInProgress: true, addByEmailError: null };
    case ADD_BY_EMAIL_SUCCESS:
      return { ...state, addByEmailInProgress: false };
    case ADD_BY_EMAIL_ERROR:
      return { ...state, addByEmailInProgress: false, addByEmailError: payload };

    case IMPORT_SUBSCRIBERS_REQUEST:
      return {
        ...state,
        importSubscribersInProgress: true,
        importSubscribersResult: null,
        importSubscribersError: null,
      };
    case IMPORT_SUBSCRIBERS_SUCCESS:
      return { ...state, importSubscribersInProgress: false, importSubscribersResult: payload };
    case IMPORT_SUBSCRIBERS_ERROR:
      return { ...state, importSubscribersInProgress: false, importSubscribersError: payload };

    case MATCH_SUBSCRIBERS_REQUEST:
      return { ...state, matchInProgress: true };
    case MATCH_SUBSCRIBERS_SUCCESS:
      return { ...state, matchInProgress: false };
    case MATCH_SUBSCRIBERS_ERROR:
      return { ...state, matchInProgress: false };

    case FETCH_AUDIENCE_USERS_SUCCESS:
      return { ...state, usersById: { ...state.usersById, ...payload } };

    case FETCH_EMAILS_REQUEST:
      return { ...state, emailsLoading: true, emailsError: null };
    case FETCH_EMAILS_SUCCESS:
      return { ...state, emailsLoading: false, emailsLoaded: true, emails: payload };
    case FETCH_EMAILS_ERROR:
      return { ...state, emailsLoading: false, emailsError: payload };

    case CREATE_EMAIL_REQUEST:
      return { ...state, createEmailInProgress: true, createEmailError: null };
    case CREATE_EMAIL_SUCCESS:
      return {
        ...state,
        createEmailInProgress: false,
        emails: payload ? [...state.emails, payload] : state.emails,
      };
    case CREATE_EMAIL_ERROR:
      return { ...state, createEmailInProgress: false, createEmailError: payload };

    case UPDATE_EMAIL_REQUEST:
      return { ...state, updateEmailInProgress: true, updateEmailError: null };
    case UPDATE_EMAIL_SUCCESS:
      return {
        ...state,
        updateEmailInProgress: false,
        emails: state.emails.map(e => (e.id === payload.id ? payload : e)),
      };
    case UPDATE_EMAIL_ERROR:
      return { ...state, updateEmailInProgress: false, updateEmailError: payload };

    case DELETE_EMAIL_REQUEST:
      return { ...state, deleteEmailInProgress: payload, deleteEmailError: null };
    case DELETE_EMAIL_SUCCESS:
      return {
        ...state,
        deleteEmailInProgress: null,
        emails: state.emails.filter(e => e.id !== payload),
      };
    case DELETE_EMAIL_ERROR:
      return { ...state, deleteEmailInProgress: null, deleteEmailError: payload };

    case SEND_EMAIL_REQUEST:
      return { ...state, sendEmailInProgress: payload, sendEmailError: null };
    case SEND_EMAIL_SUCCESS:
      return {
        ...state,
        sendEmailInProgress: null,
        emails: state.emails.map(e => (e.id === payload.id ? payload : e)),
      };
    case SEND_EMAIL_ERROR:
      return { ...state, sendEmailInProgress: null, sendEmailError: payload };

    case RESET:
      return initialState;

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setOrgId = orgId => ({ type: SET_ORG_ID, payload: orgId });

export const fetchListsRequest = () => ({ type: FETCH_LISTS_REQUEST });
export const fetchListsSuccess = lists => ({ type: FETCH_LISTS_SUCCESS, payload: lists });
export const fetchListsError = error => ({ type: FETCH_LISTS_ERROR, payload: error });

export const createListRequest = () => ({ type: CREATE_LIST_REQUEST });
export const createListSuccess = list => ({ type: CREATE_LIST_SUCCESS, payload: list });
export const createListError = error => ({ type: CREATE_LIST_ERROR, payload: error });

export const updateListRequest = () => ({ type: UPDATE_LIST_REQUEST });
export const updateListSuccess = list => ({ type: UPDATE_LIST_SUCCESS, payload: list });
export const updateListError = error => ({ type: UPDATE_LIST_ERROR, payload: error });

export const deleteListRequest = listId => ({ type: DELETE_LIST_REQUEST, payload: listId });
export const deleteListSuccess = listId => ({ type: DELETE_LIST_SUCCESS, payload: listId });
export const deleteListError = error => ({ type: DELETE_LIST_ERROR, payload: error });

export const fetchListDetailRequest = () => ({ type: FETCH_LIST_DETAIL_REQUEST });
export const fetchListDetailSuccess = list => ({ type: FETCH_LIST_DETAIL_SUCCESS, payload: list });
export const fetchListDetailError = error => ({ type: FETCH_LIST_DETAIL_ERROR, payload: error });

export const addMemberRequest = () => ({ type: ADD_MEMBER_TO_LIST_REQUEST });
export const addMemberSuccess = () => ({ type: ADD_MEMBER_TO_LIST_SUCCESS });
export const addMemberError = error => ({ type: ADD_MEMBER_TO_LIST_ERROR, payload: error });

export const clearImportErrors = () => ({ type: CLEAR_IMPORT_ERRORS });
export const importMembersRequest = () => ({ type: IMPORT_MEMBERS_REQUEST });
export const importMembersSuccess = result => ({ type: IMPORT_MEMBERS_SUCCESS, payload: result });
export const importMembersError = error => ({ type: IMPORT_MEMBERS_ERROR, payload: error });

export const fetchSubscribersRequest = () => ({ type: FETCH_SUBSCRIBERS_REQUEST });
export const fetchSubscribersSuccess = data => ({ type: FETCH_SUBSCRIBERS_SUCCESS, payload: data });
export const fetchSubscribersError = error => ({ type: FETCH_SUBSCRIBERS_ERROR, payload: error });

export const removeMemberRequest = memberId => ({ type: REMOVE_MEMBER_REQUEST, payload: memberId });
export const removeMemberSuccess = memberId => ({ type: REMOVE_MEMBER_SUCCESS, payload: memberId });
export const removeMemberError = error => ({ type: REMOVE_MEMBER_ERROR, payload: error });

export const fetchImportsRequest = () => ({ type: FETCH_IMPORTS_REQUEST });
export const fetchImportsSuccess = imports => ({ type: FETCH_IMPORTS_SUCCESS, payload: imports });
export const fetchImportsError = error => ({ type: FETCH_IMPORTS_ERROR, payload: error });

export const addByEmailRequest = () => ({ type: ADD_BY_EMAIL_REQUEST });
export const addByEmailSuccess = () => ({ type: ADD_BY_EMAIL_SUCCESS });
export const addByEmailError = error => ({ type: ADD_BY_EMAIL_ERROR, payload: error });

export const importSubscribersRequest = () => ({ type: IMPORT_SUBSCRIBERS_REQUEST });
export const importSubscribersSuccess = result => ({
  type: IMPORT_SUBSCRIBERS_SUCCESS,
  payload: result,
});
export const importSubscribersError = error => ({ type: IMPORT_SUBSCRIBERS_ERROR, payload: error });

export const matchSubscribersRequest = () => ({ type: MATCH_SUBSCRIBERS_REQUEST });
export const matchSubscribersSuccess = () => ({ type: MATCH_SUBSCRIBERS_SUCCESS });
export const matchSubscribersError = () => ({ type: MATCH_SUBSCRIBERS_ERROR });

export const fetchAudienceUsersSuccess = usersById => ({
  type: FETCH_AUDIENCE_USERS_SUCCESS,
  payload: usersById,
});

export const fetchEmailsRequest = () => ({ type: FETCH_EMAILS_REQUEST });
export const fetchEmailsSuccess = emails => ({ type: FETCH_EMAILS_SUCCESS, payload: emails });
export const fetchEmailsError = error => ({ type: FETCH_EMAILS_ERROR, payload: error });

export const createEmailRequest = () => ({ type: CREATE_EMAIL_REQUEST });
export const createEmailSuccess = email => ({ type: CREATE_EMAIL_SUCCESS, payload: email });
export const createEmailError = error => ({ type: CREATE_EMAIL_ERROR, payload: error });

export const updateEmailRequest = () => ({ type: UPDATE_EMAIL_REQUEST });
export const updateEmailSuccess = email => ({ type: UPDATE_EMAIL_SUCCESS, payload: email });
export const updateEmailError = error => ({ type: UPDATE_EMAIL_ERROR, payload: error });

export const deleteEmailRequest = emailId => ({ type: DELETE_EMAIL_REQUEST, payload: emailId });
export const deleteEmailSuccess = emailId => ({ type: DELETE_EMAIL_SUCCESS, payload: emailId });
export const deleteEmailError = error => ({ type: DELETE_EMAIL_ERROR, payload: error });

export const sendEmailRequest = emailId => ({ type: SEND_EMAIL_REQUEST, payload: emailId });
export const sendEmailSuccess = email => ({ type: SEND_EMAIL_SUCCESS, payload: email });
export const sendEmailError = error => ({ type: SEND_EMAIL_ERROR, payload: error });

// ================ Thunks ================ //

export const fetchLists = orgId => async dispatch => {
  dispatch(fetchListsRequest());
  try {
    const response = await orgAudienceAPI.getLists(orgId);
    dispatch(fetchListsSuccess(response.data?.data?.lists || []));
  } catch (error) {
    dispatch(fetchListsError(storableError(error)));
  }
};

export const createList = (orgId, listData) => async dispatch => {
  dispatch(createListRequest());
  try {
    const response = await orgAudienceAPI.createList(orgId, listData);
    // postJson uses fetch (raw JSON), so response.data is the { data: { list } } level
    const list = response.data?.list;
    dispatch(createListSuccess(list));
    return list;
  } catch (error) {
    dispatch(createListError(storableError(error)));
    throw error;
  }
};

export const updateList = (orgId, listId, updates) => async dispatch => {
  dispatch(updateListRequest());
  try {
    const response = await orgAudienceAPI.updateList(orgId, { listId, ...updates });
    const list = response.data?.list;
    dispatch(updateListSuccess(list));
    // Re-fetch list detail to get updated member list
    dispatch(fetchListDetail(orgId, listId));
  } catch (error) {
    dispatch(updateListError(storableError(error)));
    throw error;
  }
};

export const deleteList = (orgId, listId) => async dispatch => {
  dispatch(deleteListRequest(listId));
  try {
    await orgAudienceAPI.deleteList(orgId, { listId });
    dispatch(deleteListSuccess(listId));
  } catch (error) {
    dispatch(deleteListError(storableError(error)));
    throw error;
  }
};

export const fetchListDetail = (orgId, listId, silent = false) => async dispatch => {
  if (!silent) dispatch(fetchListDetailRequest());
  try {
    const response = await orgAudienceAPI.getList(orgId, listId);
    dispatch(fetchListDetailSuccess(response.data?.data?.list));
  } catch (error) {
    if (!silent) dispatch(fetchListDetailError(storableError(error)));
  }
};

export const addMemberToList = (orgId, listId, emails, attestation) => async dispatch => {
  dispatch(addMemberRequest());
  try {
    const emailList = Array.isArray(emails) ? emails : [emails].filter(Boolean);
    for (const email of emailList) {
      await orgAudienceAPI.addMember(orgId, { listId, email, attestation });
    }
    dispatch(addMemberSuccess());
    dispatch(fetchListDetail(orgId, listId, true)); // silent: skip loading state
  } catch (error) {
    dispatch(addMemberError(storableError(error)));
    throw error;
  }
};

export const importMembers = (orgId, listId, csvData, attestation) => async dispatch => {
  dispatch(importMembersRequest());
  try {
    // csvData may be { fileName, data } object or a raw string
    const csvString = typeof csvData === 'object' && csvData.data ? csvData.data : csvData;
    const response = await orgAudienceAPI.importMembers(orgId, {
      listId,
      csvData: csvString,
      attestation,
    });
    dispatch(importMembersSuccess(response.data?.summary));
    dispatch(fetchListDetail(orgId, listId, true)); // silent refresh
  } catch (error) {
    dispatch(importMembersError(storableError(error)));
    throw error;
  }
};

export const fetchSubscribers = (orgId, params) => async dispatch => {
  dispatch(fetchSubscribersRequest());
  try {
    const response = await orgAudienceAPI.query(orgId, params);
    const data = response.data?.data || { members: [], pagination: null };
    dispatch(fetchSubscribersSuccess(data));

    // Batch-fetch live Sharetribe user objects for matched subscribers
    const members = data.members || [];
    const matchedIds = members.filter(m => m.hasAccount && m.userId).map(m => m.userId);
    if (matchedIds.length > 0) {
      const userResponses = await Promise.all(matchedIds.map(id => usersAPI.show({ id })));
      const usersById = {};
      userResponses.forEach(res => {
        const user = res.data?.data;
        if (user?.id?.uuid) usersById[user.id.uuid] = user;
      });
      dispatch(fetchAudienceUsersSuccess(usersById));
    }
  } catch (error) {
    dispatch(fetchSubscribersError(storableError(error)));
  }
};

export const removeMember = (orgId, memberId) => async dispatch => {
  dispatch(removeMemberRequest(memberId));
  try {
    await orgAudienceAPI.remove(orgId, { memberId });
    dispatch(removeMemberSuccess(memberId));
  } catch (error) {
    dispatch(removeMemberError(storableError(error)));
    throw error;
  }
};

export const fetchImports = orgId => async dispatch => {
  dispatch(fetchImportsRequest());
  try {
    const response = await orgAudienceAPI.getImports(orgId);
    dispatch(fetchImportsSuccess(response.data?.data?.imports || []));
  } catch (error) {
    dispatch(fetchImportsError(storableError(error)));
  }
};

export const addSubscribersByEmail = (orgId, emailsString, attestation) => async dispatch => {
  dispatch(addByEmailRequest());
  try {
    const emails = emailsString
      .split(',')
      .map(e => e.trim())
      .filter(Boolean);
    for (const email of emails) {
      await orgAudienceAPI.add(orgId, { email, attestation });
    }
    dispatch(addByEmailSuccess());
    dispatch(fetchSubscribers(orgId));
  } catch (error) {
    dispatch(addByEmailError(storableError(error)));
    throw error;
  }
};

export const importSubscribersFile = (orgId, csvData, fileName, attestation) => async dispatch => {
  dispatch(importSubscribersRequest());
  try {
    const response = await orgAudienceAPI.import(orgId, { csvData, fileName, attestation });
    dispatch(importSubscribersSuccess(response.data?.summary));
    dispatch(fetchSubscribers(orgId));
    dispatch(fetchImports(orgId));
  } catch (error) {
    dispatch(importSubscribersError(storableError(error)));
    throw error;
  }
};

export const matchSubscribers = orgId => async (dispatch, getState) => {
  const subscribers = getState().OrgAudiencePage.subscribers;
  const needsMatching = subscribers.some(
    s => s.hasAccount == null || (s.hasAccount === true && !s.firstName && !s.profileImageUrl)
  );
  if (!needsMatching) return;
  dispatch(matchSubscribersRequest());
  try {
    await orgAudienceAPI.match(orgId);
    dispatch(matchSubscribersSuccess());
    dispatch(fetchSubscribers(orgId));
  } catch (error) {
    dispatch(matchSubscribersError());
  }
};

export const fetchEmails = orgId => async dispatch => {
  dispatch(fetchEmailsRequest());
  try {
    const response = await orgAudienceAPI.getEmails(orgId);
    dispatch(fetchEmailsSuccess(response.data?.data?.emails || []));
  } catch (error) {
    dispatch(fetchEmailsError(storableError(error)));
    throw error;
  }
};

export const createEmail = (orgId, emailData) => async dispatch => {
  dispatch(createEmailRequest());
  try {
    const response = await orgAudienceAPI.createEmail(orgId, emailData);
    const email = response?.email;
    dispatch(createEmailSuccess(email));
    return email;
  } catch (error) {
    dispatch(createEmailError(storableError(error)));
    throw error;
  }
};

export const updateEmail = (orgId, emailId, updates) => async dispatch => {
  dispatch(updateEmailRequest());
  try {
    const response = await orgAudienceAPI.updateEmail(orgId, emailId, updates);
    const email = response?.email;
    dispatch(updateEmailSuccess(email));
    return email;
  } catch (error) {
    dispatch(updateEmailError(storableError(error)));
    throw error;
  }
};

export const deleteEmail = (orgId, emailId) => async dispatch => {
  dispatch(deleteEmailRequest(emailId));
  try {
    await orgAudienceAPI.deleteEmail(orgId, emailId);
    dispatch(deleteEmailSuccess(emailId));
  } catch (error) {
    dispatch(deleteEmailError(storableError(error)));
    throw error;
  }
};

export const sendEmail = (orgId, emailId) => async dispatch => {
  dispatch(sendEmailRequest(emailId));
  try {
    const response = await orgAudienceAPI.sendEmail(orgId, emailId);
    const email = response?.email;
    dispatch(sendEmailSuccess(email));
    return email;
  } catch (error) {
    dispatch(sendEmailError(storableError(error)));
    throw error;
  }
};

// ================ Selectors ================ //

export const selectLists = state => state.OrgAudiencePage.lists;
export const selectListsLoading = state => state.OrgAudiencePage.listsLoading;
export const selectListsLoaded = state => state.OrgAudiencePage.listsLoaded;
export const selectListsError = state => state.OrgAudiencePage.listsError;
export const selectCreateListInProgress = state => state.OrgAudiencePage.createListInProgress;
export const selectDeleteListInProgress = state => state.OrgAudiencePage.deleteListInProgress;
export const selectSelectedList = state => state.OrgAudiencePage.selectedList;
export const selectSelectedListLoading = state => state.OrgAudiencePage.selectedListLoading;
export const selectUpdateListInProgress = state => state.OrgAudiencePage.updateListInProgress;
export const selectAddMemberInProgress = state => state.OrgAudiencePage.addMemberInProgress;
export const selectImportInProgress = state => state.OrgAudiencePage.importInProgress;
export const selectImportError = state => state.OrgAudiencePage.importError;
export const selectImportSubscribersError = state => state.OrgAudiencePage.importSubscribersError;
export const selectImportResult = state => state.OrgAudiencePage.importResult;
export const selectSubscribers = state => state.OrgAudiencePage.subscribers;
export const selectSubscribersLoading = state => state.OrgAudiencePage.subscribersLoading;
export const selectSubscribersLoaded = state => state.OrgAudiencePage.subscribersLoaded;
export const selectSubscribersError = state => state.OrgAudiencePage.subscribersError;
export const selectSubscribersPagination = state => state.OrgAudiencePage.subscribersPagination;
export const selectRemoveMemberInProgress = state => state.OrgAudiencePage.removeMemberInProgress;
export const selectImports = state => state.OrgAudiencePage.imports;
export const selectImportsLoading = state => state.OrgAudiencePage.importsLoading;
export const selectAddByEmailInProgress = state => state.OrgAudiencePage.addByEmailInProgress;
export const selectImportSubscribersInProgress = state =>
  state.OrgAudiencePage.importSubscribersInProgress;
export const selectUsersById = state => state.OrgAudiencePage.usersById;
export const selectEmails = state => state.OrgAudiencePage.emails;
export const selectEmailsLoading = state => state.OrgAudiencePage.emailsLoading;
export const selectEmailsLoaded = state => state.OrgAudiencePage.emailsLoaded;
export const selectEmailsError = state => state.OrgAudiencePage.emailsError;
export const selectCreateEmailInProgress = state => state.OrgAudiencePage.createEmailInProgress;
export const selectUpdateEmailInProgress = state => state.OrgAudiencePage.updateEmailInProgress;
export const selectDeleteEmailInProgress = state => state.OrgAudiencePage.deleteEmailInProgress;
export const selectSendEmailInProgress = state => state.OrgAudiencePage.sendEmailInProgress;
export const selectSendEmailError = state => state.OrgAudiencePage.sendEmailError;

// ================ loadData ================ //

/**
 * Page-mount fetches for Org Audience: lists, subscribers, and email
 * campaigns for the active org. Fired in parallel by the route's
 * loadData hook so the page mounts with redux already hydrated.
 *
 * Replaces the prior mount-time useEffect inside the component (project
 * rule: data fetching goes through loadData + the duck, never useEffect
 * + API client inside a component).
 */
export const loadData = params => dispatch => {
  const orgId = params?.orgId;
  if (!orgId) return Promise.resolve();
  // Reset the slice when the org actually changes so the previous org's
  // audience data can't flash under the new org during its fetches.
  dispatch(setOrgId(orgId));
  return Promise.all([
    dispatch(fetchLists(orgId)),
    dispatch(fetchSubscribers(orgId)),
    dispatch(fetchEmails(orgId)),
  ]);
};
